// On load, if this app has no token yet, ask the common login page (hidden
// iframe) whether the user already has a gateway session — e.g. from signing
// in on another HikmahVerses product — and adopt it. Runs at most once per tab.
const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || "http://localhost:3007/login";

export function trySilentSso({ hasToken, setToken }) {
  try {
    if (hasToken() || sessionStorage.getItem("hv_sso_checked")) return;
    if (window.location.pathname.startsWith("/sso/")) return;
    sessionStorage.setItem("hv_sso_checked", "1");
    const login = new URL(LOGIN_URL);
    const frame = document.createElement("iframe");
    frame.style.display = "none";
    frame.src = `${login.origin}${login.pathname}?silent=1&parent=${encodeURIComponent(window.location.origin)}`;
    const done = () => { window.removeEventListener("message", onMessage); frame.remove(); };
    const onMessage = (e) => {
      if (e.origin !== login.origin || e.data?.type !== "hv-sso") return;
      done();
      if (e.data.token) {
        setToken(e.data.token);
        window.location.reload();
      }
    };
    window.addEventListener("message", onMessage);
    setTimeout(done, 8000); // no answer — stay signed out
    document.body.appendChild(frame);
  } catch { /* storage blocked etc. — silent check is best-effort */ }
}

// ---- Recovery after a 401 ---------------------------------------------------
// The app can look signed in (the gateway cookie authorises GETs) while its own
// saved token is missing, expired or rejected, so every POST/PUT/DELETE 401s.
// On such a 401 ask the common login page once whether a session still exists:
//   - yes  -> adopt the fresh token and reload
//   - no   -> drop the stale token and reload, so the app shows signed out
// A cooldown stops this from ever looping.
const RECOVER_KEY = "hv_sso_recover";
const RECOVER_COOLDOWN_MS = 30000;
let recovering = false;

export function handleUnauthorized({ url = "", method = "GET", hadToken, setToken, clearToken }) {
  try {
    // 401 from the "who am I" / sign-in calls is normal for a signed-out visitor.
    if (/\/auth\/(me|login|register|logout|google)/.test(url)) return;
    // A signed-out visitor's plain GETs are expected to 401 — nothing to repair.
    if (!hadToken && method.toUpperCase() === "GET") return;
    if (recovering || window.location.pathname.startsWith("/sso/")) return;
    if (Date.now() - Number(sessionStorage.getItem(RECOVER_KEY) || 0) < RECOVER_COOLDOWN_MS) return;
    sessionStorage.setItem(RECOVER_KEY, String(Date.now()));
    recovering = true;

    const login = new URL(LOGIN_URL);
    const frame = document.createElement("iframe");
    frame.style.display = "none";
    frame.src = `${login.origin}${login.pathname}?silent=1&parent=${encodeURIComponent(window.location.origin)}`;
    const finish = (token) => {
      window.removeEventListener("message", onMessage);
      frame.remove();
      recovering = false;
      if (token) { setToken(token); window.location.reload(); }
      else if (hadToken) { clearToken(); window.location.reload(); }
    };
    const onMessage = (e) => {
      if (e.origin !== login.origin || e.data?.type !== "hv-sso") return;
      finish(e.data.token || null);
    };
    window.addEventListener("message", onMessage);
    setTimeout(() => { if (recovering) finish(null); }, 8000);
    document.body.appendChild(frame);
  } catch { recovering = false; }
}
